// Cluster insights — stretches where SEVERAL deficiency runs sit together.
// Works with or without crash data: with crashes it reports how many crashes
// (and correlated crashes) fell on the cluster and the best-value
// countermeasure; without, it still ranks the multi-deficiency stretches.
import React, { useEffect, useMemo, useState } from 'react';
import { INSIGHT_SORTS, sortClusters } from './safetyFilters';
import { fmtUsd } from './insightsText';
import { Stars } from './CmfCard';
import { GoToReport } from './InsightsPanel';
import TopNSelect from './TopNSelect';
import { NET_CONDITIONS } from '../SafetyOverview/rules';

const PSD_REASON_WORDS = {
  unsafe: 'unsafe pass: passing sight distance short where the marking allows passing',
  intersection: 'passing zone within the SOP distance of an intersection',
  shortZone: 'passing zone shorter than the SOP minimum',
  lowSpeed: 'passing zone below the SOP minimum speed',
  ...Object.fromEntries(NET_CONDITIONS.map(([k, , near]) => [k, `passing zone within the SOP distance of ${near}`])),
};

const fmtMp = (v) => (v === null || v === undefined ? '—' : v.toFixed(3));
const DIR = { P: 'EB', N: 'WB' };

const ANALYSIS_WORDS = {
  SSD: (a) => `stopping sight distance short (worst ${a.worstValue} mph speed deficit)`,
  // a PSD run counts for its reasons: an Unsafe pass, or a passing zone the SOP forbids (near an
  // intersection or a road feature, too short, too slow) where the sight distance itself is fine
  PSD: (a) => (a.reasons && a.reasons.length
    ? a.reasons.map((r) => PSD_REASON_WORDS[r] || r).join('; ')
    : `passing sight distance short (worst ${a.worstValue} mph)`),
  GLR: (a) => `sun-glare impact (${a.worstValue} glare-min/yr)`,
  FSA: (a) => `flat spot / poor drainage (${a.worstValue}% gradient)`,
  HCA: (a) => `curve${a.curveNumber != null ? ` #${a.curveNumber}` : ''} below safety score (${a.worstValue}%)`,
  GRW: (a) => `guardrail warranted (${a.worstValue} ft embankment)`,
  ISD: (a) => `intersection sight distance (${a.worstValue} ft required)`,
};

/** One line per analysis of a cluster: what RDV found there (also the PDF's cluster insights). */
export const clusterAnalysesText = (cl) => Object.entries(cl.analyses)
  .map(([k, a]) => `${k} — ${(ANALYSIS_WORDS[k] || (() => k))(a)}${a.runs > 1 ? ` (${a.runs} runs)` : ''} ${a.directions.map((d) => DIR[d] || d).join('/')}`);

export default function ClusterInsightsPanel({ clusters, onFocus, recommendations, hasCrashData, analysis, crashNoun = 'crash',
  defaultSort = 'mp', weights = null, sortTitle = null }) {
  const [sortBy, setSortBy] = useState(defaultSort);
  const [limit, setLimit] = useState(0);            // step through the top N clusters of the sort (0 = all)
  const sorted = useMemo(() => sortClusters(clusters || [], sortBy, weights), [clusters, sortBy, weights]);
  const ordered = useMemo(() => (limit ? sorted.slice(0, limit) : sorted), [sorted, limit]);
  const [idx, setIdx] = useState(0);
  useEffect(() => { setIdx(0); }, [analysis, ordered.length, sortBy]);

  if (!ordered.length) {
    return (
      <div className="safety-muted">
        No deficiency clusters in the current selection — no two deficiency runs sit within 0.1 mi
        of each other{analysis !== 'ALL' ? ` for ${analysis}. Switch the analysis to "All analyses" to see multi-analysis clusters.` : '.'}
      </div>
    );
  }
  const i = Math.min(idx, ordered.length - 1);
  const cl = ordered[i];
  const go = (next) => {
    const j = (i + next + ordered.length) % ordered.length;
    setIdx(j);
    const c = ordered[j];
    // Prev / Next point the map at the cluster; "Show on map" also brings the map into view
    if (onFocus) onFocus({ bounds: c.bounds, lat: c.lat, lng: c.lng, key: `dcluster-${c.id}-${Date.now()}` }, { scroll: false });
  };
  const analysesText = clusterAnalysesText(cl);
  // report deep links: one per analysis in the cluster (first run that has one)
  const reports = Object.keys(cl.analyses)
    .map((k) => { const run = cl.runs.find((r) => r.analysis === k && r.reportUrl); return run ? [k, { reportUrl: run.reportUrl }] : null; })
    .filter(Boolean);
  // best-value countermeasure among the cluster's sites
  const best = (recommendations || [])
    .filter((r) => cl.runKeys.includes(r.key) && r.entry && r.benefitPerYr > 0)
    .sort((a, b) => b.benefitPerYr - a.benefitPerYr)[0];
  const totals = ordered.reduce((s, c) => ({
    crashes: s.crashes + c.crashCount, correlated: s.correlated + c.correlatedCount,
    fatal: s.fatal + c.fatalCount, cost: s.cost + c.cost,
  }), { crashes: 0, correlated: 0, fatal: 0, cost: 0 });

  return (
    <div className="safety-insights">
      <div className="safety-insights-nav">
        <button type="button" className="safety-btn" onClick={() => go(-1)}>‹ Prev</button>
        <span className="safety-insights-counter">Cluster {i + 1} of {ordered.length}{ordered.length < sorted.length ? ` (top of ${sorted.length})` : ''}</span>
        <button type="button" className="safety-btn" onClick={() => go(1)}>Next ›</button>
        <button type="button" className="safety-btn safety-btn-ghost"
          onClick={() => onFocus && onFocus({ bounds: cl.bounds, lat: cl.lat, lng: cl.lng, key: `dcluster-${cl.id}-${Date.now()}` })}>
          Show on map
        </button>
        <label className="safety-filter safety-filter-inline" title={sortBy === 'worst' && sortTitle ? sortTitle : undefined}>
          <span>Sort</span>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            {INSIGHT_SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </label>
        <label className="safety-filter safety-filter-inline" title="Step through only the top clusters of the current sort">
          <span>Show</span>
          <TopNSelect value={limit} onChange={setLimit} max={sorted.length} className="" />
        </label>
      </div>

      <div className="safety-insight-card safety-insight-card-cluster">
        <div className="safety-insight-lrs">
          {cl.road ? `${cl.road} · ` : ''}MP {fmtMp(cl.mpStart)}–{fmtMp(cl.mpEnd)}
          <span className="safety-insight-date"> · {cl.lengthMi} mi · {cl.runCount} deficiency runs · {cl.analysisCount} analys{cl.analysisCount === 1 ? 'is' : 'es'}</span>
        </div>
        <p className="safety-insight-crash">
          RDV found {cl.analysisCount === 1 ? 'repeated' : 'overlapping'} deficiencies on this stretch:
        </p>
        <ul className="safety-insight-findings">
          {analysesText.map((t) => <li key={t}>{t}</li>)}
        </ul>
        {hasCrashData ? (
          <p className="safety-insight-crash safety-insight-cluster-crashes">
            {cl.crashCount === 0
              ? `No ${crashNoun}es were recorded on this stretch in the loaded years — a deficiency cluster without crash history is a candidate for proactive treatment.`
              : <>
                <strong className="safety-key">{cl.crashCount} {crashNoun}{cl.crashCount === 1 ? '' : 'es'}</strong> occurred here
                ({cl.mix.K} K · {cl.mix.A} A · {cl.mix.B} B · {cl.mix.C} C · {cl.mix.O} O),
                of which <strong className="safety-key">{cl.correlatedCount} {cl.correlatedCount === 1 ? 'is' : 'are'} correlated</strong> with one of these
                deficiencies — <strong className="safety-key">comprehensive cost {fmtUsd(cl.cost)}</strong>
                {cl.fatalCount > 0 ? <> including <strong className="safety-key">{cl.fatalCount} fatal</strong></> : null}.
              </>}
          </p>
        ) : (
          <p className="safety-muted">Crash data hidden or unavailable — cluster ranked by the number and severity of its deficiencies.</p>
        )}
        {best && (
          <p className="safety-insight-crash">
            Best-value countermeasure here: <strong className="safety-key">{best.entry.name}</strong> ({best.analysis} site, CMF {best.entry.cmf}{' '}
            <Stars entry={best.entry} />) — {best.preventedPerYr} crashes prevented/yr,{' '}
            <span className="safety-benefit">{fmtUsd(best.benefitPerYr)}/yr</span>
            {best.bcRatio != null ? `, B/C ${best.bcRatio}` : ''}.
          </p>
        )}
        {reports.length > 0 && (
          <div className="safety-insight-actions">
            <GoToReport findings={reports} />
          </div>
        )}
      </div>

      <div className="safety-insight-summary">
        <p>
          <strong className="safety-key">{ordered.length} deficiency cluster{ordered.length === 1 ? '' : 's'}</strong> in this selection
          {hasCrashData ? <> hold <strong className="safety-key">{totals.crashes} {crashNoun}es</strong> ({totals.correlated} correlated,
            {' '}{totals.fatal} fatal) with a <strong className="safety-key">comprehensive cost of {fmtUsd(totals.cost)}</strong>.</> : '.'}
          {' '}Clusters are stretches where two or more deficiency runs lie within 0.1 mi of each other
          (any direction); "Worst offenders" ranks them by crash cost, then by the number and severity of
          the deficiencies.
        </p>
      </div>
    </div>
  );
}
